import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { sanitizeText } from 'utils/inputSanitization';
import './FeedbackModal.css';

export type FeedbackType = 'bug_report' | 'feature_request' | 'general';

const MAX_IMAGES = 3;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FeedbackData) => void;
  isLoading?: boolean;
}

export interface FeedbackData {
  feedbackType: FeedbackType;
  // Bug Report fields
  overview?: string;
  currentBehavior?: string;
  desiredBehavior?: string;
  // Feature Request fields
  featureOverview?: string;
  coreProblem?: string;
  proposedSolution?: string;
  // General Feedback fields
  sentiment?: 'frustrated' | 'neutral' | 'happy';
  generalFeedback?: string;
  responsePreference?: 'yes' | 'no' | 'no_preference';
  // Image attachments
  images?: File[];
}

const FeedbackModal: React.FC<Props> = ({ isOpen, onClose, onSubmit, isLoading = false }) => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug_report');
  const [formData, setFormData] = useState<FeedbackData>({
    feedbackType: 'bug_report',
    overview: '',
    currentBehavior: '',
    desiredBehavior: '',
    featureOverview: '',
    coreProblem: '',
    proposedSolution: '',
    sentiment: 'neutral',
    generalFeedback: '',
    responsePreference: 'no_preference'
  });
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFeedbackType('bug_report');
      setFormData({
        feedbackType: 'bug_report',
        overview: '',
        currentBehavior: '',
        desiredBehavior: '',
        featureOverview: '',
        coreProblem: '',
        proposedSolution: '',
        sentiment: 'neutral',
        generalFeedback: '',
        responsePreference: 'no_preference'
      });
      // Clear images and revoke URLs
      imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
      setPendingImages([]);
      setImagePreviewUrls([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const addFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;

    const remainingSlots = MAX_IMAGES - pendingImages.length;
    if (remainingSlots <= 0) {
      toast.warning(`Maximum ${MAX_IMAGES} images per feedback`);
      return;
    }

    const filesToAdd: File[] = [];
    const newPreviewUrls: string[] = [];

    for (const file of files) {
      if (filesToAdd.length >= remainingSlots) {
        toast.warning(`Only ${remainingSlots} more image(s) can be added`);
        break;
      }

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error(`"${file.name}" is not supported. Only JPEG, PNG, and WEBP images are allowed.`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`"${file.name}" is too large. Maximum file size is 5MB.`);
        continue;
      }

      filesToAdd.push(file);
      newPreviewUrls.push(URL.createObjectURL(file));
    }

    if (filesToAdd.length > 0) {
      setPendingImages(prev => [...prev, ...filesToAdd]);
      setImagePreviewUrls(prev => [...prev, ...newPreviewUrls]);
    }
  }, [pendingImages.length]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  }, [addFiles]);

  const handleRemoveImage = useCallback((index: number) => {
    URL.revokeObjectURL(imagePreviewUrls[index]);
    setPendingImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  }, [imagePreviewUrls]);

  const handleSubmit = () => {
    const dataToSubmit: FeedbackData = {
      ...formData,
      feedbackType,
      images: pendingImages.length > 0 ? pendingImages : undefined
    };
    onSubmit(dataToSubmit);
  };

  const handleTypeChange = (type: FeedbackType) => {
    setFeedbackType(type);
    setFormData(prev => ({ ...prev, feedbackType: type }));
  };

  const updateField = (field: keyof FeedbackData, value: string) => {
    const sanitizedValue = field === 'responsePreference' || field === 'sentiment'
      ? value
      : sanitizeText(value);
    setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
  };

  if (!isOpen) return null;

  const imageUploadSection = (
    <div className="feedback-form-group">
      <label className="feedback-label">Screenshots (optional)</label>

      {imagePreviewUrls.length > 0 && (
        <div className="feedback-image-preview-strip">
          {imagePreviewUrls.map((url, index) => (
            <div key={url} className="feedback-image-preview-item">
              <img src={url} alt={`Preview ${index + 1}`} className="feedback-image-preview-thumbnail" />
              <button
                className="feedback-image-preview-remove"
                onClick={() => handleRemoveImage(index)}
                type="button"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="feedback-image-actions">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <button
          className="feedback-image-attach-button"
          onClick={() => fileInputRef.current?.click()}
          type="button"
          title="Attach images"
          disabled={pendingImages.length >= MAX_IMAGES}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <span>Attach images</span>
        </button>
        {pendingImages.length > 0 && (
          <span className="feedback-image-count">
            {pendingImages.length}/{MAX_IMAGES}
          </span>
        )}
      </div>
    </div>
  );

  const modalContent = (
    <div className="feedback-modal-overlay" onClick={onClose}>
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        <div className="feedback-modal-header">
          <h2 className="feedback-modal-title">Send feedback</h2>
          <button className="feedback-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="feedback-modal-content">
          {/* Feedback Type Selector */}
          <div className="feedback-form-group">
            <label className="feedback-label">Feedback type</label>
            <select
              className="feedback-select"
              value={feedbackType}
              onChange={(e) => handleTypeChange(e.target.value as FeedbackType)}
            >
              <option value="bug_report">Bug Report</option>
              <option value="feature_request">Feature Request</option>
              <option value="general">General Feedback</option>
            </select>
          </div>

          {/* Bug Report Fields */}
          {feedbackType === 'bug_report' && (
            <>
              <div className="feedback-form-group">
                <label className="feedback-label">Overview of the issue:</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Summarize the issue in a sentence or two."
                  value={formData.overview}
                  onChange={(e) => updateField('overview', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="feedback-form-group">
                <label className="feedback-label">Current behavior</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Summarize the issue in a sentence or two."
                  value={formData.currentBehavior}
                  onChange={(e) => updateField('currentBehavior', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="feedback-form-group">
                <label className="feedback-label">Desired behavior:</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Summarize the issue in a sentence or two."
                  value={formData.desiredBehavior}
                  onChange={(e) => updateField('desiredBehavior', e.target.value)}
                  rows={3}
                />
              </div>
            </>
          )}

          {/* Feature Request Fields */}
          {feedbackType === 'feature_request' && (
            <>
              <div className="feedback-form-group">
                <label className="feedback-label">Overview of feature</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Summarize the feature in a sentence or two."
                  value={formData.featureOverview}
                  onChange={(e) => updateField('featureOverview', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="feedback-form-group">
                <label className="feedback-label">Core problem:</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="What is the underlying issue or struggle you're facing?"
                  value={formData.coreProblem}
                  onChange={(e) => updateField('coreProblem', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="feedback-form-group">
                <label className="feedback-label">Proposed solution:</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Describe your solution to the core problem"
                  value={formData.proposedSolution}
                  onChange={(e) => updateField('proposedSolution', e.target.value)}
                  rows={3}
                />
              </div>
            </>
          )}

          {/* General Feedback Fields */}
          {feedbackType === 'general' && (
            <>
              <div className="feedback-form-group">
                <label className="feedback-label">How are you feeling?</label>
                <div className="feedback-sentiment-buttons">
                  <button
                    className={`feedback-sentiment-btn ${formData.sentiment === 'frustrated' ? 'selected' : ''}`}
                    onClick={() => updateField('sentiment', 'frustrated')}
                  >
                    😤
                  </button>
                  <button
                    className={`feedback-sentiment-btn ${formData.sentiment === 'neutral' ? 'selected' : ''}`}
                    onClick={() => updateField('sentiment', 'neutral')}
                  >
                    😐
                  </button>
                  <button
                    className={`feedback-sentiment-btn ${formData.sentiment === 'happy' ? 'selected' : ''}`}
                    onClick={() => updateField('sentiment', 'happy')}
                  >
                    😊
                  </button>
                </div>
              </div>

              <div className="feedback-form-group">
                <label className="feedback-label">Feedback:</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="What's on your mind?"
                  value={formData.generalFeedback}
                  onChange={(e) => updateField('generalFeedback', e.target.value)}
                  rows={5}
                />
              </div>

              <div className="feedback-form-group">
                <label className="feedback-label">Would you like a response to this feedback?</label>
                <select
                  className="feedback-select"
                  value={formData.responsePreference}
                  onChange={(e) => updateField('responsePreference', e.target.value as 'yes' | 'no' | 'no_preference')}
                >
                  <option value="no_preference">No preference</option>
                  <option value="yes">Yes, please respond</option>
                  <option value="no">No response needed</option>
                </select>
              </div>
            </>
          )}

          {/* Image Upload Section - shown for all feedback types */}
          {imageUploadSection}
        </div>

        <div className="feedback-modal-footer">
          <MagicButton
            label={isLoading ? "Sending..." : "Send Feedback"}
            color="purple"
            onPress={handleSubmit}
            isDisabled={isLoading}
            isLoading={isLoading}
            fullWidth
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default FeedbackModal;
